<template>
  <Panel
    class="p-0 edit-results-modal position-relative"
    :class="{
      disabled: isLoading,
    }"
    :variant="['transparent']"
  >
    <Panel
      :key="`${match.id}-info`"
      :variant="['transparent']"
      class="p-0 mb-0 d-flex align-items-center justify-content-between"
    >
      <TextLabel v-if="match.battle_id" class="p-0 mt-3 battle-id-label">
        {{
          $t('_web_tournament_battle_id', '# {match}', {
            match: match.index + 1,
          })
        }}
        | {{ roundName }}
      </TextLabel>
      <StatusLabel :status="match.status" class="px-4 py-3" />
    </Panel>
    <div
      class="d-flex my-5 align-items-center justify-content-center position-relative"
    >
      <div class="d-flex w-100 players-left-wrapper justify-content-end">
        <div
          class="flex-grow-0 text-center h-mr-auto"
          :class="{
            disabled: !canEdit || editMode,
            'cursor-pointer': canEdit && !editMode,
          }"
          @click="setTechWinner(1)"
        >
          Set as<br />winner
        </div>
        <PlayerItem
          class="p-0 flex-grow-1 flex-shrink-0 align-items-center justify-content-center"
          :player="match.player_1"
          :is-stacked="true"
          hide-checkin
          hide-score
          :avatar-size="48"
          :player-score="playerOneScore"
          :player-index="1"
          :variant="playerOneVariant"
          :is-profile="isSingle"
          :chat-with="match.captain_1"
          :is-ready-for-battle="!match.is_detained || match.player_1_ready"
          :is-empty="match.player_empty_1"
        />
      </div>
      <Scores
        :value="summaryScores"
        class="flex-shrink-0 flex-nowrap mb-5 players-separator mx-3"
      />
      <div class="d-flex players-right-wrapper w-100">
        <PlayerItem
          class="p-0 flex-grow-1 flex-shrink-0 flex-row-reverse align-items-center justify-content-center"
          :player="match.player_2"
          :is-stacked="true"
          hide-checkin
          hide-score
          :avatar-size="48"
          :player-score="playerTwoScore"
          :player-index="2"
          :variant="playerTwoVariant"
          :is-profile="isSingle"
          :chat-with="match.captain_2"
          :is-ready-for-battle="!match.is_detained || match.player_2_ready"
          :is-empty="match.player_empty_2"
        />
        <div
          class="flex-grow-0 text-center h-ml-auto cursor-pointer"
          :class="{
            disabled: !canEdit || editMode,
            'cursor-pointer': canEdit && !editMode,
          }"
          @click="setTechWinner(2)"
        >
          Set as<br />winner
        </div>
      </div>
    </div>
    <BattleResults
      :battle="match"
      :edit-mode.sync="editMode"
      :is-match-conflicted="isMatchConflicted(match)"
      @allowSaveResult="isSaveDisabled = !$event"
    />
    <div class="d-flex align-items-center font-weight-bold my-5">
      <Icon
        name="calendar"
        width="20"
        :fill="$scss.colors.secondaryTextBg"
        class="h-mr-3"
      />
      <TextBlock :size="0.75" :variant="'secondary'" :line-height="1"
        >{{ match.date_start | localDate($times.DATE_SUBMED) }},</TextBlock
      >
      <TextBlock
        class="h-ml-3"
        :size="0.75"
        :variant="'secondary'"
        :line-height="1"
        >{{ match.date_start | localDate($times.TIME_SIMPLE) }}</TextBlock
      >
      <TextBlock :variant="'secondary'" :size="0.75" :line-height="1"
        >&nbsp;&ndash;&nbsp;</TextBlock
      >
      <TextBlock :variant="'secondary'" :size="0.75" :line-height="1">{{
        match.date_end | localDate($times.TIME_SIMPLE)
      }}</TextBlock>
    </div>
    <div class="controls d-flex justify-content-center">
      <HeraFormBtn
        class="btn btn-secondary-simple"
        :label="$t('_web_tournaments-tableview_battle-close-btn', 'Close')"
        @click="$emit('cancel')"
      />
      <HeraFormBtn
        :disabled="isSaveDisabled || !editMode"
        class="btn btn-sm btn-accent-simple h-ml-4"
        :class="{
          disabled: isSaveDisabled || !editMode,
        }"
        :label="resultEditingBtnLabel"
        @click="saveBattleResults"
      />
      <InfoTooltip
        class="h-ml-auto position-absolute edit-results-toggle"
        :show-icon="false"
        :text="$t('_web_tournaments-tableview_battle-edit-btn', 'Edit results')"
      >
        <Panel
          class="cursor-pointer p-3"
          :disabled="!match.id || !canEdit"
          :class="{
            disabled: !match.id || !canEdit,
          }"
          :variant="['transparent', 'rounded', 'rounded-medium']"
          @click.stop="toggleEditMode"
        >
          <Icon name="edit" />
        </Panel>
      </InfoTooltip>
    </div>
  </Panel>
</template>

<script>
import { mapGetters } from 'vuex';
import Panel from '@/components/atoms/Panel';
import { BATTLE_STATUSES } from '@/constants/battles';
import { getPlayerScore } from '@/views/tournaments/utils/players';
import PlayerItem from '@/components/tournaments/playerItem.vue';
import { prop } from '@/utils/factories';
import StatusLabel from '@/components/molecules/StatusLabel';
import TextBlock from '@/components/atoms/TextBlock';
import TextLabel from '@/components/atoms/TextLabel';
import Icon from '@/components/atoms/Icon';
import BattleResults from '@/components/tournaments/Results.vue';
import matchHelper from '@/views/tournaments/management/brackets/mixins/matchHelper';
import loadingHelper from '@/mixins/isLoading';
import HeraFormBtn from '@/components/atoms/HeraFormBtn';
import Scores from '@/components/tournaments/Scores.vue';
import InfoTooltip from '@/components/molecules/InfoTooltip';
export default {
  name: 'DuelMatchCardEdit',
  components: {
    Panel,
    PlayerItem,
    StatusLabel,
    TextBlock,
    Icon,
    TextLabel,
    BattleResults,
    HeraFormBtn,
    Scores,
    InfoTooltip,
  },
  mixins: [matchHelper, loadingHelper],
  props: {
    roundName: prop(String),
    canEdit: prop(Boolean),
    match: {
      type: Object,
      default: () => ({
        date_end: null,
        date_start: null,
        id: '',
        index: 0,
        next_match_id: '',
        next_match_index: 0,
        player_1: {},
        player_1_score: null,
        player_2: {},
        player_2_score: null,
        round: 0,
        isLoading: false,
      }),
    },
  },
  data() {
    return {
      isSaveDisabled: false,
      editMode: false,
    };
  },
  computed: {
    ...mapGetters([
      'currentTournamentStage',
      'hasMatchTieResults',
      'hasMatchTieGame',
    ]),
    resultEditingBtnLabel() {
      return this.isMatchConflicted(this.match)
        ? this.$t('_web_tournaments-tableview_batle-judge-btn', 'Resolve')
        : this.$t('_web_tournaments-tableview_batle-save-btn', 'Save results');
    },
    matchID() {
      return this.match.id;
    },
    summaryScores() {
      return this.isMatchConflicted(this.match)
        ? {
            player_1_scored: '?',
            player_2_scored: '?',
          }
        : {
            player_1_scored: this.playerOneScore,
            player_2_scored: this.playerTwoScore,
          };
    },
    playerOneScore() {
      return getPlayerScore(this.match, 1);
    },

    playerTwoScore() {
      return getPlayerScore(this.match, 2);
    },
    isMatchCancelled() {
      return this.match.status === BATTLE_STATUSES.cancelled;
    },
    playerOneVariant() {
      return this.match.is_detained && !this.match.player_1_ready
        ? 'error'
        : 'transparent';
    },

    playerTwoVariant() {
      return this.match.is_detained && !this.match.player_2_ready
        ? 'error'
        : 'transparent';
    },
    isSingle() {
      return (
        this.match && this.match.player_1 && this.match.player_1.type !== 'clan'
      );
    },
    isScheduled() {
      return this.match.status === 'scheduled';
    },
  },
  watch: {
    match() {
      this.unsetLoading();
    },
  },
  methods: {
    saveBattleResults() {
      this.setLoading();
      const hasTieGame = this.hasMatchTieGame(this.match.battle_id);
      if (
        hasTieGame &&
        !this.currentTournamentStage.block_configuration.tie_game
      ) {
        this.errorNotify({
          message: this.$t(
            '_web_tournaments-tie-game-not-allowed',
            'Tie results are not allowed for this competition'
          ),
          inModal: true,
          yesText: this.$t('_web_form_error_confirm-yes', 'OK'),
        });
        this.unsetLoading();
        return;
      }
      const hasTieMatch = this.hasMatchTieResults(this.match.battle_id);
      if (
        hasTieMatch &&
        !this.currentTournamentStage.block_configuration.tie_match
      ) {
        this.errorNotify({
          message: this.$t(
            '_web_tournaments-tie-match-not-allowed',
            'Tie match are not allowed for this competition'
          ),
          inModal: true,
          yesText: this.$t('_web_form_error_confirm-yes', 'OK'),
        });
        this.unsetLoading();
        return;
      }
      this.$emit('resolved', this.match);
    },
    setTechWinner(playerIndex) {
      this.setLoading();
      this.$emit('techWin', playerIndex);
    },
    toggleEditMode() {
      this.editMode = !this.editMode;
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-results-modal {
  min-width: 450px;
  .edit-results-toggle {
    @include auto-rtl(right, 0);
  }
  .players-left-wrapper,
  .players-right-wrapper {
    max-width: calc(50% - 45px);
  }
  .players-separator {
    width: 90px;
  }
}
// .match-wrapper--details {
//   min-height: $match-item-height;
//   max-width: 400px;
//   transition: all 0.2s ease-in;
//   .match-card {
//     z-index: 1;
//     width: 400px;
//     &_duel-score {
//       min-width: 108px;
//       flex-basis: 108px;
//       height: 40px;
//     }
//     .player-wrapper--vs-mode {
//       flex-basis: calc(50% - 54px);
//       max-width: calc(50% - 54px);
//     }
//     .battle-id-label {
//       @include auto-rtl(left, 0);
//     }
//   }
// }
</style>
